// modules
import { useState } from 'react';
import { Box, Collapse, Typography, Grid } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// local imports
import { LINK_BLUE, LIGHT_GRAY } from '../../constants/colors';
import flag from '../../assets/uswds/us_flag_small.png';
import iconDotGov from '../../assets/uswds/icon-dot-gov.svg';
import iconHTTPS from '../../assets/uswds/icon-https.svg';

export default function USWDSBanner() {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Box
      id="uswds-banner"
      component="section"
      aria-label="Official website of the United States government"
      sx={{
        width: '100%',
        backgroundColor: LIGHT_GRAY,
        borderBottom: 0,
        borderColor: 'divider',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1024,
          px: { xs: 0, md: 1 },
          boxSizing: 'border-box',
        }}
      >
        {/* Banner Header */}
        <Box
          sx={{
            px: 2.1,
            py: 0.5,
            height: { xs: 48, sm: 22 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 0, sm: 0 },
            paddingTop: { xs: '6px', sm: 0.5 },
          }}
        >
          {/* Flag and Text */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.95,
              marginBottom: { xs: 0, sm: '0.1rem' },
            }}
          >
            <Box
              component="img"
              src={flag}
              alt=""
              aria-hidden="true"
              width={16}
              height={11}
              sx={{ width: 16, height: 11, marginTop: '2px' }}
            />
            <Typography
              variant="caption"
              sx={{
                whiteSpace: 'nowrap',
                fontSize: '0.8rem',
                lineHeight: '0.8rem',
                marginTop: { xs: '1px', sm: 0 },
              }}
            >
              An official website of the United States government
            </Typography>
          </Box>

          {/* Clickable "Here's how you know" */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              ml: { xs: '23px', sm: 1 },
              marginTop: { xs: '1px', sm: 0 },
            }}
          >
            <Box
              role="button"
              onClick={handleToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggle();
                }
              }}
              tabIndex={0}
              aria-expanded={expanded}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                marginBottom: '0.05rem',
                outline: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              <Typography
                variant="caption"
                component="span"
                sx={{
                  fontSize: '0.8rem',
                  lineHeight: '0.8rem',
                  textDecoration: 'underline',
                  color: LINK_BLUE,
                  marginLeft: '0rem',
                }}
              >
                Here's how you know
              </Typography>
            </Box>
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                color: LINK_BLUE,
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                marginLeft: '0.15rem',
                marginTop: '0.1rem',
              }}
            />
          </Box>
        </Box>

        {/* Collapsible Content */}
        <Collapse in={expanded} timeout={0} unmountOnExit>
          <Box
            id="gov-banner-content"
            sx={{
              p: 2,
              minHeight: 144,
              boxSizing: 'border-box',
            }}
          >
            <Grid container spacing={2}>
              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{ display: 'flex', alignItems: 'flex-start' }}
              >
                <Box
                  component="img"
                  src={iconDotGov}
                  alt="banner dot-gov icon"
                  sx={{ width: 40, height: 40, mr: 1 }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                    <strong>Official websites use .gov</strong>
                    <br />A <strong>.gov</strong> website belongs to an official
                    government organization in the United States.
                  </Typography>
                </Box>
              </Grid>
              <Grid
                size={{ xs: 12, md: 6 }}
                sx={{ display: 'flex', alignItems: 'flex-start' }}
              >
                <Box
                  component="img"
                  src={iconHTTPS}
                  alt=""
                  aria-hidden="true"
                  sx={{ width: 40, height: 40, mr: 1 }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                    <strong>Secure .gov websites use HTTPS</strong>
                    <br />A lock (
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', verticalAlign: 'middle' }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="13"
                        viewBox="0 0 52 64"
                        role="img"
                        aria-labelledby="banner-lock-description-default"
                        focusable="false"
                      >
                        <title id="banner-lock-title-default">Lock</title>
                        <desc id="banner-lock-description-default">
                          Locked padlock icon
                        </desc>
                        <path
                          fill="currentColor"
                          fillRule="evenodd"
                          d="M26 0c10.493 0 19 8.507 19 19v9h3a4 4 0 0 1 4 4v28a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V32a4 4 0 0 1 4-4h3v-9C7 8.507 15.507 0 26 0zm0 8c-5.979 0-10.843 4.77-10.996 10.712L15 19v9h22v-9c0-6.075-4.925-11-11-11z"
                        />
                      </svg>
                    </Box>
                    ) or <strong>https://</strong> means you've safely connected
                    to the .gov website. Share sensitive information only on
                    official, secure websites.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}
